import React from 'react';
import { Controller } from 'react-hook-form';
import { Box, Typography, Grid, TextField, MenuItem, Autocomplete, IconButton, Button, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { dayOfWeek } from 'constants/day';
import { SORT_TYPE_PRESORT, SORT_TYPE_FINAL } from 'constants/warehouseConfig';
import { transitionRules } from 'hooks/Warehouse/useWarehouseConfigForm';
import { parseRegionList, parseWarehouseIdList } from 'utils/warehouseTransitions';

const SORT_TYPE_OPTIONS = [SORT_TYPE_PRESORT, SORT_TYPE_FINAL].map((type) => ({ value: type, label: type }));

const renderDeliveryDaysInput = (params) => <TextField {...params} placeholder="Select days" />;

const fieldError = (formState, index, field) => formState.errors?.transitions?.[index]?.[field]?.message;

const duplicateWarning = (parse, noun, value) => {
  const { duplicates } = parse(value);
  if (duplicates.length === 0) return null;
  return `Duplicate ${noun} will be saved once: ${[...new Set(duplicates)].join(', ')}`;
};

function EditWarehouseTransition({
  control,
  formState,
  fields,
  addTransition,
  removeTransition,
  applyDefaultLabelVersion,
}) {
  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Transition Configuration</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color="primary" size="medium" disableElevation onClick={addTransition}>
          Add Config
        </Button>
      </Box>
      <Box sx={{ mt: 2 }}>
        {fields.length === 0 && <Typography sx={{ textAlign: 'center' }}>No transition config</Typography>}
        {fields.map((row, index) => (
          <Box key={row.key} sx={{ position: 'relative', mb: 2 }}>
            <Grid container spacing={0.5} sx={{ mr: 2, alignItems: 'stretch' }}>
              <Grid size={{ xs: 12, sm: 2 }}>
                <Typography variant="subtitle2">
                  Sort Type <strong style={{ color: 'red' }}>*</strong>
                </Typography>
                <Controller
                  control={control}
                  name={`transitions.${index}.sort_type`}
                  rules={transitionRules('sort_type')}
                  render={({ field: { ref, ...field } }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      select
                      fullWidth
                      size="small"
                      error={!!fieldError(formState, index, 'sort_type')}
                      helperText={fieldError(formState, index, 'sort_type') ?? ''}
                      onChange={(event) => {
                        field.onChange(event);
                        applyDefaultLabelVersion(index, event.target.value);
                      }}
                    >
                      {SORT_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <Typography variant="subtitle2">
                  Target Regions <strong style={{ color: 'red' }}>*</strong>
                </Typography>
                <Controller
                  control={control}
                  name={`transitions.${index}.target_regions`}
                  rules={transitionRules('target_regions')}
                  render={({ field: { ref, ...field } }) => {
                    const error = fieldError(formState, index, 'target_regions');
                    const warning = error ? null : duplicateWarning(parseRegionList, 'regions', field.value);
                    return (
                      <TextField
                        {...field}
                        inputRef={ref}
                        fullWidth
                        size="small"
                        error={!!error}
                        helperText={error ?? warning ?? 'Separate regions by comma or space'}
                        slotProps={{
                          formHelperText: { sx: { ml: 0, ...(warning ? { color: 'warning.main' } : {}) } },
                        }}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="subtitle2">Target Warehouses</Typography>
                <Controller
                  control={control}
                  name={`transitions.${index}.target_warehouses`}
                  rules={transitionRules('target_warehouses')}
                  render={({ field: { ref, ...field } }) => {
                    const error = fieldError(formState, index, 'target_warehouses');
                    const warning = error ? null : duplicateWarning(parseWarehouseIdList, 'IDs', field.value);
                    return (
                      <TextField
                        {...field}
                        inputRef={ref}
                        fullWidth
                        size="small"
                        error={!!error}
                        helperText={error ?? warning ?? 'Separate warehouse IDs by comma or space'}
                        slotProps={{
                          formHelperText: { sx: { ml: 0, ...(warning ? { color: 'warning.main' } : {}) } },
                        }}
                      />
                    );
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Typography variant="subtitle2">Delivery Days</Typography>
                <Controller
                  control={control}
                  name={`transitions.${index}.delivery_days`}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      size="small"
                      options={dayOfWeek}
                      limitTags={2}
                      disableCloseOnSelect
                      filterSelectedOptions
                      getOptionLabel={(option) => option.value}
                      value={dayOfWeek.filter((day) => field.value?.includes(day.value))}
                      onChange={(_, newValue) => field.onChange(newValue.map((day) => day.value))}
                      renderInput={renderDeliveryDaysInput}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 2 }}>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2">
                      Label Version <strong style={{ color: 'red' }}>*</strong>
                    </Typography>
                    <Controller
                      control={control}
                      name={`transitions.${index}.label_version`}
                      rules={transitionRules('label_version')}
                      render={({ field: { ref, ...field } }) => (
                        <TextField
                          {...field}
                          inputRef={ref}
                          fullWidth
                          size="small"
                          error={!!fieldError(formState, index, 'label_version')}
                          helperText={fieldError(formState, index, 'label_version') ?? ''}
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', height: '40px', alignItems: 'center' }}>
                    <IconButton size="small" color="error" onClick={() => removeTransition(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default EditWarehouseTransition;
